import { Footer, Wrapper } from 'components'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  Button,
  Card,
  Input,
  toast,
  Upload,
  UploadBody,
  UploadText,
} from 'keep-react'
import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Download, TrashSimple } from 'phosphor-react'
import { brokerService } from 'api/services/broker'
import { fileService } from 'api/services/file'

const validationSchema = Yup.object().shape({
  company: Yup.string().required('Este campo é obrigatório'),
  nif: Yup.string().required('Este campo é obrigatório'),
  mobile: Yup.string().required('Este campo é obrigatório'),
  adminName: Yup.string().required('Este campo é obrigatório'),
  email: Yup.string().email().required('EEste campo é obrigatório'),
  files: Yup.array().test(
    'has-all-files',
    'Todos os ficheiros são obrigatórios',
    files => {
      return Array.isArray(files) && files.every(file => file !== null)
    },
  ),
})

export const Register = () => {
  const [isCallingApi, setIsCallingApi] = useState<boolean>(false)
  const [fichaNomeacao, setFichaNomeacao] = useState<string | null>(null)
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {
      company: '',
      nif: '',
      mobile: '',
      adminName: '',
      email: '',
      files: Array(5).fill(null),
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      setIsCallingApi(true)
      try {
        const brokerRequestID = Number(
          await brokerService.saveBrokerReq({
            company: values.company,
            nif: values.nif,
            mobile: values.mobile,
            adminName: values.adminName,
            email: values.email,
          }),
        )

        if (!brokerRequestID) {
          throw new Error('Broker Request ID is invalid')
          toast.error('Erro ao fazer o registo.')
        } else {
          try {
            await brokerService.addBrokerReqFiles({
              brokerRequestID,
              files: values.files.filter(file => file !== null) as File[],
            })
            toast.success('Registo efetuado com sucesso!')
            navigate('/login')
          } catch (err) {
            toast.error('Erro ao fazer o upload dos ficheiros.')
          }
        }
      } catch (err) {
        toast.error('Erro ao fazer o registo.')
      } finally {
        setIsCallingApi(false)
      }
    },
  })

  const onDrop = async <T extends File>(acceptedFiles: T[], index?: number) => {
    // validate image type
    if (acceptedFiles[0].type !== 'application/pdf') {
      toast.error(
        'Formato de ficheiro inválido. Por favor, envie um ficheiro PDF.',
      )
      return
    }

    if (index !== undefined && acceptedFiles.length > 0) {
      const newFiles = [...formik.values.files]
      newFiles[index] = acceptedFiles[0]
      formik.setFieldValue('files', newFiles)
    }
  }

  const getFile = async () => {
    try {
      const response = await fileService.getFilesFromAzure({
        identifier: 'ficha_nomeacao',
        type: 'settings',
      })

      if (response[0]) {
        setFichaNomeacao(response[0].uri)
      }
    } catch (err) {
      console.error('Erro ao obter a ficha de nomeação:', err)
    }
  }

  useEffect(() => {
    getFile()
  }, [])

  return (
    <div
      className="w-screen h-screen bg-cover bg-no-repeat bg-center flex items-center"
      style={{
        backgroundImage: `url(images/dark_broker.jpg)`,
      }}
    >
      <Wrapper>
        <div className="flex flex-col items-center max-w-[1000px] m-auto gap-y-12 h-full">
          <Card className="w-full bg-white/70 p-4 rounded-xl max-w-none">
            <h2 className="font-light text-heading-6 text-primary-400 pb-4 uppercase">
              Junte-se à nossa equipa!
            </h2>
            <div className="text-body-4 space-y-2 text-metal-600 pb-6">
              <p>
                Dê o próximo passo com o Innova|GO. Leve os nossos produtos
                especializados para a sua carteira de forma simples:
              </p>
              <p>
                Basta ser mediador certificado, preencher a Ficha de Nomeação e
                completar o formulário ao lado com os seus documentos.
              </p>

              <p>Torne o seu dia a dia mais ágil.</p>
              <p>Especializados. Por Si!</p>
            </div>
            <form onSubmit={formik.handleSubmit} className="space-y-5">
              <div>
                <h3 className="text-body-4 font-bold text-metal-600 pb-1">
                  Dados
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1 col-span-2">
                    <Input
                      type="text"
                      name="company"
                      onChange={formik.handleChange}
                      placeholder="Empresa"
                      className="px-2"
                      value={formik.values.company}
                    />
                    {formik.errors.company && (
                      <div className="text-red-500 text-body-5">
                        {formik.errors.company}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Input
                      type="text"
                      name="nif"
                      onChange={formik.handleChange}
                      placeholder="NIF"
                      className="px-2 w-full"
                      value={formik.values.nif}
                    />
                    {formik.errors.nif && (
                      <div className="text-red-500 text-body-5">
                        {formik.errors.nif}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Input
                      type="text"
                      name="mobile"
                      onChange={formik.handleChange}
                      placeholder="Telemóvel"
                      className="px-2 w-full"
                      value={formik.values.mobile}
                    />
                    {formik.errors.mobile && (
                      <div className="text-red-500 text-body-5">
                        {formik.errors.mobile}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Input
                      type="text"
                      name="adminName"
                      onChange={formik.handleChange}
                      placeholder="Nome do Administrador"
                      className="px-2 w-full"
                      value={formik.values.adminName}
                    />
                    {formik.errors.adminName && (
                      <div className="text-red-500 text-body-5">
                        {formik.errors.adminName}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Input
                      type="email"
                      name="email"
                      onChange={formik.handleChange}
                      placeholder="Email"
                      className="px-2 w-full"
                      value={formik.values.email}
                    />
                    {formik.errors.email && (
                      <div className="text-red-500 text-body-5">
                        {formik.errors.email}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-body-4 font-bold text-metal-600 pb-1">
                  Ficheiros
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {formik.values.files &&
                  formik.values.files[0] &&
                  formik.values.files[0].name ? (
                    <div className="flex justify-between gap-2 items-center w-full bg-white rounded-[10px] px-2">
                      <p className="text-body-4 font-normal text-metal-900 my-2">
                        {formik.values.files[0].name}
                      </p>
                      <Button
                        shape="circle"
                        className="h-7 w-7"
                        variant="softBg"
                        onClick={() => {
                          const newFiles = [...formik.values.files]
                          newFiles[0] = null
                          formik.setFieldValue('files', newFiles)
                        }}
                      >
                        <TrashSimple size={16} />
                      </Button>
                    </div>
                  ) : (
                    <Upload
                      horizontal
                      className="p-0"
                      options={{
                        onDrop: acceptedFiles => onDrop(acceptedFiles, 0),
                        multiple: false,
                      }}
                    >
                      <UploadBody className="border-0 py-0">
                        <UploadText className="text-body-4 font-normal text-metal-400 my-2">
                          Ficha de nomeação preenchida (PDF)
                        </UploadText>
                      </UploadBody>
                    </Upload>
                  )}
                  <div className="flex items-center gap-2 underline text-body-4">
                    {fichaNomeacao && (
                      <div className="flex gap-2">
                        <Download size={16} className="text-metal-900" />
                        <a
                          href="/pdf/pedido_nomeacao.pdf"
                          className="text-metal-900"
                        >
                          Ficha de nomeação para preencher
                        </a>
                      </div>
                    )}
                  </div>
                  {formik.values.files &&
                  formik.values.files[1] &&
                  formik.values.files[1].name ? (
                    <div className="flex justify-between gap-2 items-center w-full bg-white rounded-[10px] px-2">
                      <p className="text-body-4 font-normal text-metal-900 my-2">
                        {formik.values.files[1].name}
                      </p>
                      <Button
                        shape="circle"
                        className="h-7 w-7"
                        variant="softBg"
                        onClick={() => {
                          const newFiles = [...formik.values.files]
                          newFiles[1] = null
                          formik.setFieldValue('files', newFiles)
                        }}
                      >
                        <TrashSimple size={16} />
                      </Button>
                    </div>
                  ) : (
                    <Upload
                      horizontal
                      className="p-0"
                      options={{
                        onDrop: acceptedFiles => onDrop(acceptedFiles, 1),
                        multiple: false,
                      }}
                    >
                      <UploadBody className="border-0 py-0">
                        <UploadText className="text-body-4 font-normal text-metal-400 my-2">
                          Apólice de Responsabilidade Civil (PDF)
                        </UploadText>
                      </UploadBody>
                    </Upload>
                  )}

                  {formik.values.files &&
                  formik.values.files[2] &&
                  formik.values.files[2].name ? (
                    <div className="flex justify-between gap-2 items-center w-full bg-white rounded-[10px] px-2">
                      <p className="text-body-4 font-normal text-metal-900 my-2">
                        {formik.values.files[2].name}
                      </p>
                      <Button
                        shape="circle"
                        className="h-7 w-7"
                        variant="softBg"
                        onClick={() => {
                          const newFiles = [...formik.values.files]
                          newFiles[2] = null
                          formik.setFieldValue('files', newFiles)
                        }}
                      >
                        <TrashSimple size={16} />
                      </Button>
                    </div>
                  ) : (
                    <Upload
                      horizontal
                      className="p-0"
                      options={{
                        onDrop: acceptedFiles => onDrop(acceptedFiles, 2),
                        multiple: false,
                      }}
                    >
                      <UploadBody className="border-0 py-0">
                        <UploadText className="text-body-4 font-normal text-metal-400 my-2">
                          Relatório e Contas (PDF)
                        </UploadText>
                      </UploadBody>
                    </Upload>
                  )}

                  {formik.values.files &&
                  formik.values.files[3] &&
                  formik.values.files[3].name ? (
                    <div className="flex justify-between gap-2 items-center w-full bg-white rounded-[10px] px-2">
                      <p className="text-body-4 font-normal text-metal-900 my-2">
                        {formik.values.files[3].name}
                      </p>
                      <Button
                        shape="circle"
                        className="h-7 w-7"
                        variant="softBg"
                        onClick={() => {
                          const newFiles = [...formik.values.files]
                          newFiles[3] = null
                          formik.setFieldValue('files', newFiles)
                        }}
                      >
                        <TrashSimple size={16} />
                      </Button>
                    </div>
                  ) : (
                    <Upload
                      horizontal
                      className="p-0"
                      options={{
                        onDrop: acceptedFiles => onDrop(acceptedFiles, 3),
                        multiple: false,
                      }}
                    >
                      <UploadBody className="border-0 py-0">
                        <UploadText className="text-body-4 font-normal text-metal-400 my-2">
                          Certificado de ASF de Mediador (PDF)
                        </UploadText>
                      </UploadBody>
                    </Upload>
                  )}

                  {formik.values.files &&
                  formik.values.files[4] &&
                  formik.values.files[4].name ? (
                    <div className="flex justify-between gap-2 items-center w-full bg-white rounded-[10px] px-2">
                      <p className="text-body-4 font-normal text-metal-900 my-2">
                        {formik.values.files[4].name}
                      </p>
                      <Button
                        shape="circle"
                        className="h-7 w-7"
                        variant="softBg"
                        onClick={() => {
                          const newFiles = [...formik.values.files]
                          newFiles[4] = null
                          formik.setFieldValue('files', newFiles)
                        }}
                      >
                        <TrashSimple size={16} />
                      </Button>
                    </div>
                  ) : (
                    <Upload
                      horizontal
                      className="p-0"
                      options={{
                        onDrop: acceptedFiles => onDrop(acceptedFiles, 4),
                        multiple: false,
                      }}
                    >
                      <UploadBody className="border-0 py-0">
                        <UploadText className="text-body-4 font-normal text-metal-400 my-2">
                          IBAN (PDF)
                        </UploadText>
                      </UploadBody>
                    </Upload>
                  )}
                </div>
                {typeof formik.errors.files === 'string' && (
                  <div className="text-red-500 text-body-5">
                    {formik.errors.files}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  className="w-full"
                  type="submit"
                  disabled={isCallingApi}
                >
                  Pedir Nomeação
                </Button>

                <Button
                  className="w-full"
                  type="submit"
                  disabled={isCallingApi}
                  onClick={() => navigate('/login')}
                >
                  Voltar ao Login
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </Wrapper>
      <Footer />
    </div>
  )
}
